import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import type { NotificationStreamEvent } from '@/api/notificationStream';
import type { NotificationItem, NotificationPage } from '@/types/notifications';
import { notificationKeys } from './keys';

export type NotificationListData = InfiniteData<NotificationPage, string>;

/** Kinds the stream carries for other consumers; they never belong in the bell. */
const NON_BELL_KINDS = new Set(['challenge_confirmed']);

export interface NotificationCacheSnapshot {
  lists: [readonly unknown[], NotificationListData | undefined][];
  count: number | undefined;
}

function mapItems(
  data: NotificationListData,
  fn: (items: NotificationItem[]) => NotificationItem[],
): NotificationListData {
  return { ...data, pages: data.pages.map((page) => ({ ...page, items: fn(page.items) })) };
}

function listQueries(client: QueryClient) {
  return client.getQueriesData<NotificationListData>({ queryKey: notificationKeys.lists() });
}

function isUnreadOnly(queryKey: readonly unknown[]): boolean {
  const filter = queryKey[2] as { unreadOnly?: boolean } | undefined;

  return filter?.unreadOnly === true;
}

function findItem(client: QueryClient, id: string): NotificationItem | undefined {
  for (const [, data] of listQueries(client)) {
    const found = data?.pages.flatMap((p) => p.items).find((item) => item.id === id);
    if (found) {
      return found;
    }
  }

  return undefined;
}

export function snapshotNotifications(client: QueryClient): NotificationCacheSnapshot {
  return {
    lists: listQueries(client),
    count: client.getQueryData<number>(notificationKeys.unreadCount()),
  };
}

export function restoreNotifications(client: QueryClient, snapshot: NotificationCacheSnapshot) {
  for (const [key, data] of snapshot.lists) {
    client.setQueryData(key, data);
  }
  client.setQueryData(notificationKeys.unreadCount(), snapshot.count);
}

/** Marks one item read in every cached list and drops the unread count by one. */
export function applyMarkRead(client: QueryClient, id: string, readAt = new Date().toISOString()) {
  const target = findItem(client, id);
  const wasUnread = target !== undefined && target.read_at === null;

  for (const [key, data] of listQueries(client)) {
    if (!data) {
      continue;
    }

    client.setQueryData(
      key,
      mapItems(data, (items) =>
        isUnreadOnly(key)
          ? items.filter((item) => item.id !== id)
          : items.map((item) =>
              item.id === id && item.read_at === null ? { ...item, read_at: readAt } : item,
            ),
      ),
    );
  }

  if (wasUnread) {
    client.setQueryData<number>(notificationKeys.unreadCount(), (count) =>
      count === undefined ? count : Math.max(0, count - 1),
    );
  }
}

/** Marks every cached item read and sets the unread count to zero. */
export function applyMarkAllRead(client: QueryClient, readAt = new Date().toISOString()) {
  for (const [key, data] of listQueries(client)) {
    if (!data) {
      continue;
    }

    client.setQueryData(
      key,
      mapItems(data, (items) =>
        isUnreadOnly(key)
          ? []
          : items.map((item) => (item.read_at === null ? { ...item, read_at: readAt } : item)),
      ),
    );
  }

  client.setQueryData<number>(notificationKeys.unreadCount(), 0);
}

/** True when a stream event is a bell item. */
export function isBellEvent(event: NotificationStreamEvent): boolean {
  return event.in_bell !== false && !NON_BELL_KINDS.has(event.kind);
}

export function eventToItem(event: NotificationStreamEvent): NotificationItem {
  return {
    id: event.id,
    kind: event.kind,
    title: event.title ?? '',
    message: event.message ?? '',
    link: event.link ?? '',
    payload: event.payload ?? null,
    created_at: new Date(event.created_at * 1000).toISOString(),
    read_at: null,
  };
}

/**
 * Prepends a live bell item to the first page of each cached list and raises the
 * unread count. An item already in the cache is not added twice.
 */
export function applyStreamEvent(client: QueryClient, event: NotificationStreamEvent) {
  if (!isBellEvent(event)) {
    return;
  }

  const item = eventToItem(event);
  const known = findItem(client, item.id) !== undefined;

  for (const [key, data] of listQueries(client)) {
    if (!data || data.pages.length === 0) {
      continue;
    }

    if (data.pages.some((page) => page.items.some((existing) => existing.id === item.id))) {
      continue;
    }

    const [first, ...rest] = data.pages;
    client.setQueryData(key, {
      ...data,
      pages: [{ ...first, items: [item, ...first.items] }, ...rest],
    });
  }

  if (!known) {
    client.setQueryData<number>(notificationKeys.unreadCount(), (count) =>
      count === undefined ? count : count + 1,
    );
  }
}
